import { useRef, useState, useCallback, useEffect } from 'react';
import { synthesizeSpeech } from '../api/tts';

// Hook for synthesizing and playing speech. Exposes play/stop plus
// loading/playing/error state so buttons can reflect progress.
export function useTts() {
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  }, []);

  const play = useCallback(async (text) => {
    setError(null);
    stop();
    try {
      setLoading(true);
      const url = await synthesizeSpeech(text);
      setLoading(false);

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => {
        setPlaying(false);
        setError('Playback failed');
      };
      setPlaying(true);
      await audio.play();
    } catch (err) {
      setLoading(false);
      setPlaying(false);
      setError(err.message || 'TTS failed');
    }
  }, [stop]);

  // Stop any audio when the component using this hook unmounts.
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
  }, []);

  return { play, stop, loading, playing, error };
}
